/**
 * State-tax deadlines: the one source (spec 2026-09-30, sections 5.1 and 8.5).
 *
 * The old copies disagreed: Wyoming was July 1 flat in the renewal path,
 * March 1 in package checkout and the anniversary month on the landing page;
 * package checkout put Delaware LLC on March 1. What the states actually say:
 *
 * - Wyoming annual report (LLC and corporation): due the first day of the
 *   formation (anniversary) month, and Wyoming names the report by the year it
 *   is due. The first report is due in the year AFTER formation. It can be
 *   filed up to 120 days early.
 * - Delaware LLC annual tax (6 Del. C. 18-1107): due June 1 of the year after
 *   the tax year. The first tax year is the formation year.
 * - Delaware corporation annual report and franchise tax (8 Del. C. 502, 503):
 *   due March 1 of the year after the tax year. The first tax year is the
 *   formation year.
 *
 * Dates are ISO `YYYY-MM-DD` strings and all day math runs in UTC, so a
 * server, a browser and a Worker agree in every timezone. `today` is always a
 * parameter; nothing here reads a clock.
 */

import type { EntityKind, StateCode } from "./entity";

export interface CompanyDates {
  state: StateCode;
  entity: EntityKind;
  /** ISO `YYYY-MM-DD`. */
  formationDate: string;
}

export interface CompanyYear extends CompanyDates {
  taxYear: number;
}

// ---------------------------------------------------------------------------
// UTC-safe ISO date helpers
// ---------------------------------------------------------------------------

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 86_400_000;

interface Ymd {
  year: number;
  month: number;
  day: number;
}

function parseIso(iso: string): Ymd {
  const match = typeof iso === "string" ? ISO_DATE.exec(iso) : null;
  if (!match) throw new Error(`Invalid ISO date: ${String(iso)}`);
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const check = new Date(Date.UTC(year, month - 1, day));
  if (
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== day
  ) {
    throw new Error(`Invalid ISO date: ${iso}`);
  }
  return { year, month, day };
}

function utcMs(iso: string): number {
  const { year, month, day } = parseIso(iso);
  return Date.UTC(year, month - 1, day);
}

function pad(value: number, width: number): string {
  return String(value).padStart(width, "0");
}

/** `YYYY-MM-DD` from parts (month 1-12). */
export function isoDate(year: number, month: number, day: number): string {
  return `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`;
}

/** Add (or, negative, subtract) whole days to an ISO date. */
export function addDays(iso: string, days: number): string {
  const date = new Date(utcMs(iso) + days * MS_PER_DAY);
  return isoDate(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
  );
}

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: string, to: string): number {
  return Math.round((utcMs(to) - utcMs(from)) / MS_PER_DAY);
}

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

/** How many days before the due date the Wyoming registry accepts the report. */
export const WY_FILING_WINDOW_DAYS = 120;

/**
 * The first year a filing is owed. Wyoming: the year after formation (the
 * report is named by the year it is due). Delaware, LLC and corporation: the
 * formation year.
 */
export function firstOwedYear(company: CompanyDates): number {
  const { year } = parseIso(company.formationDate);
  return company.state === "WY" ? year + 1 : year;
}

/**
 * The due date of the filing for `taxYear`. For Wyoming `taxYear` is the
 * report year (the year the report is due).
 */
export function dueDate(input: CompanyYear): string {
  const { taxYear } = input;
  if (input.state === "WY") {
    const { month } = parseIso(input.formationDate);
    return isoDate(taxYear, month, 1);
  }
  return input.entity === "corporation"
    ? isoDate(taxYear + 1, 3, 1)
    : isoDate(taxYear + 1, 6, 1);
}

export interface FilingWindow {
  /** First day the state accepts the filing. */
  opensOn: string;
  dueDate: string;
}

/**
 * When a filing can be made. Wyoming opens 120 days before the due date;
 * Delaware opens January 1 of the year after the tax year. The window never
 * closes: a late filing is made the same way.
 */
export function filingWindow(input: CompanyYear): FilingWindow {
  const due = dueDate(input);
  const opensOn =
    input.state === "WY"
      ? addDays(due, -WY_FILING_WINDOW_DAYS)
      : isoDate(input.taxYear + 1, 1, 1);
  return { opensOn, dueDate: due };
}

/** True from the day the window opens on, whether or not the due date passed. */
export function isFilingWindowOpen(input: CompanyYear, today: string): boolean {
  return daysBetween(filingWindow(input).opensOn, today) >= 0;
}

/**
 * How many days before a filing's due date a customer may order (and pay for)
 * it. Owner rule 2026-10-01, the same for Wyoming and Delaware, LLC and
 * corporation. This is OUR ordering window, not the state's: `filingWindow` is
 * when the state accepts the filing. Reminders fire at 90, 30 and 7 days, so the
 * first reminder and the opening of the window are the same day.
 */
export const ORDER_WINDOW_DAYS = 90;

/** The first day a customer can order the filing due on `due` (ISO date). */
export function orderWindowOpensOn(due: string): string {
  return addDays(due, -ORDER_WINDOW_DAYS);
}

/**
 * A customer can order a filing from 90 days before its due date, through the
 * due date and after it (an overdue or currently-due year stays orderable).
 * Orders the system creates (renewal, package) do not go through this check.
 */
export function isOrderable(due: string, today: string): boolean {
  return daysBetween(orderWindowOpensOn(due), today) >= 0;
}

/**
 * The filing year whose due date is the next one on or after `today`, never
 * earlier than `firstOwedYear`. On the due date itself the year is still
 * current; the day after it rolls to the next year.
 *
 * - Wyoming: the report year (Hareword, formed 2023-12-22, on 2026-09-30:
 *   2026, due 2026-12-01; on 2026-12-02: 2027).
 * - Delaware LLC: before June 1 the previous year, else this year.
 * - Delaware corporation: before March 1 the previous year, else this year.
 *   (Both are "up to and including the due date".)
 */
export function currentOwedYear(company: CompanyDates, today: string): number {
  const { year } = parseIso(today);
  let candidate: number;
  if (company.state === "WY") {
    candidate = year;
    if (daysBetween(today, dueDate({ ...company, taxYear: year })) < 0) {
      candidate = year + 1;
    }
  } else {
    // Delaware tax year Y is due in Y+1, so today's due-date year is `year`
    // for tax year year-1, and `year + 1` for tax year `year`.
    candidate = year - 1;
    if (daysBetween(today, dueDate({ ...company, taxYear: candidate })) < 0) {
      candidate = year;
    }
  }
  return Math.max(candidate, firstOwedYear(company));
}

export interface OwedYear {
  taxYear: number;
  dueDate: string;
  /** The due date is before today. */
  overdue: boolean;
}

export interface OwedYearsInput {
  company: CompanyDates;
  today: string;
  /** Years the state shows as filed. */
  filedYears: Iterable<number>;
  /** Years that already have an order. */
  orderYears: Iterable<number>;
  /** Years the customer says they filed themselves. */
  selfFiledYears: Iterable<number>;
  /** How far back from the current owed year to look. Default 3. */
  lookbackYears?: number;
  /** Also list the current owed year (never overdue) when it is not covered. */
  includeCurrent?: boolean;
}

/**
 * The years still owed that come BEFORE the current owed year, from
 * `max(firstOwedYear, currentYear - lookbackYears)`, minus every year that is
 * filed, ordered or self-filed. The current owed year is reported by
 * `currentOwedYear` and only listed here with `includeCurrent`. So Hareword on
 * 2026-09-30 with 2024 and 2025 filed owes nothing yet, and its current owed
 * year is 2026, due 2026-12-01.
 */
export function owedYears(input: OwedYearsInput): OwedYear[] {
  const { company, today } = input;
  const covered = new Set<number>([
    ...input.filedYears,
    ...input.orderYears,
    ...input.selfFiledYears,
  ]);
  const current = currentOwedYear(company, today);
  const lookback = input.lookbackYears ?? 3;
  const start = Math.max(firstOwedYear(company), current - lookback);
  const last = input.includeCurrent ? current : current - 1;

  const result: OwedYear[] = [];
  for (let taxYear = start; taxYear <= last; taxYear += 1) {
    if (covered.has(taxYear)) continue;
    const due = dueDate({ ...company, taxYear });
    result.push({
      taxYear,
      dueDate: due,
      overdue: daysBetween(today, due) < 0,
    });
  }
  return result;
}
